import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppointmentBookingService,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfirmationFollowupModel,
    ConnectivityService,
    EventService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Subject, switchMap } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'pace-confirmation-followup',
    templateUrl: './confirmation-followup.component.html',
    styleUrls: ['./confirmation-followup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationFollowupComponent implements OnInit, OnDestroy, AfterViewChecked {
    @Input()
    public model: ConfirmationFollowupModel;

    @Input()
    public parentForm: FormArray;

    // form
    public followupForm: FormGroup;
    public followupFormChildren: FormArray;

    public strings: {
        followupHeader: string;
    };

    public showFollowUpAppointment: boolean;
    public showUMRCodeDropdown: boolean;
    public isFollowUpSelectedNo: boolean;
    private destroy$ = new Subject<void>();
    protected readonly faCalendar = faCalendar;
    private datesEnabledSubject = new BehaviorSubject<Date[]>([]);
    protected datesEnabled$ = this.datesEnabledSubject.asObservable();

    public constructor(
        private readonly translate: TranslateService,
        private readonly eventService: EventService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly formBuilder: FormBuilder,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly appointmentBookingService: AppointmentBookingService,
        private readonly connectivityService: ConnectivityService,
        private readonly appNotificationService: AppNotificationService,
    ) {}

    public ngOnInit(): void {
        this.strings = {
            followupHeader: <string>this.translate.instant('CONFIRMATION_FOLLOWUP_LABEL'),
        };

        this.followupFormChildren = this.formBuilder.array([]);
        this.followupForm = this.formBuilder.group({
            children: this.followupFormChildren,
        });

        this.parentForm.push(this.followupForm);

        this.eventService.materialConsumptionOrNotReceivedChanged$
            .pipe(takeUntil(this.destroy$))
            .subscribe((materialList) => {
                this.model.materialsList = materialList;
                this.determineFollowupDependency();
                this.changeDetector.detectChanges();
            });
        this.eventService.equipmentStatusAfterFixChanged$
            .pipe(takeUntil(this.destroy$))
            .subscribe((equipmentStatusAfterFix) => {
                if (typeof this.model.equipmentStatus !== 'undefined') {
                    this.model.equipmentStatus.equipmentAfterFix = equipmentStatusAfterFix;
                }
                this.determineFollowupDependency();
                this.changeDetector.detectChanges();
            });
        this.eventService.equipmentStatusInArrivalChanged$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isEquipmentDependentSA) => {
                this.model.equipmentStatus = isEquipmentDependentSA;
                this.determineFollowupDependency();
                this.changeDetector.detectChanges();
            });

        if (this.model.enableAppointmentBooking) {
            this.updateEnabledDates();
            this.connectivityService.isOnline$.pipe(takeUntil(this.destroy$)).subscribe((isOnline) => {
                if (!isOnline) {
                    this.resetAppointment();
                }
            });
        }
    }

    public ngAfterViewChecked(): void {
        this.showHideControls();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public showHideControls(): void {
        this.showFollowUpAppointment =
            this.model.followupOptions.selectedEntry.value === ConfigConstants.optionValueYes;
        this.isFollowUpSelectedNo = this.model.followupOptions.selectedEntry.value === ConfigConstants.optionValueNo;
        if (!this.isFollowUpSelectedNo) {
            if (this.model.reasonsCode && this.model.reasonsCode.selectedEntry) {
                if (
                    this.model.reasonsCode.selectedEntry.payload.codeGroup.value.indexOf(this.model.assayIndicator) ===
                    0
                ) {
                    // clear the dependent input fields
                    this.model.assayNumber.value = '';
                    this.model.lotNumber.value = '';
                }
                // deselect the selected item in tree model
                this.model.reasonsCode.selectEntry(this.model.reasonsCode.selectedEntry);
            } else if (this.model.reasonsCodeFallback && this.model.reasonsCodeFallback.selectedEntry) {
                this.model.reasonsCodeFallback.deselect();
            }
            if (this.model.showLegalCheckHandover) {
                const initialSelection = 2;
                this.model.legalCheckHandoverUserDocumentation.selectByIndex(initialSelection);
                this.model.legalCheckHandoverSafetyInformation.selectByIndex(initialSelection);
            }
        }
        if (typeof this.model.followUpUMRCodes !== 'undefined') {
            this.showUMRCodeDropdown = this.model.followupOptions.selectedEntry.value === ConfigConstants.optionValueNo;
            if (!this.showUMRCodeDropdown) {
                this.model.followUpUMRCodes.deselect();
            } else {
                // set the selected entry again
                if (this.model.followUpUMRCodes) {
                    this.model.followUpUMRCodes.restore();
                }
            }
        }
        if (typeof this.model.followUpReasons !== 'undefined' && !this.showFollowUpAppointment) {
            this.model.followUpReasons.deselect();
        } else {
            // set the selected entry again
            if (this.model.followUpReasons) {
                this.model.followUpReasons.restore();
            }
        }
        if (typeof this.model.additionalInfo !== 'undefined') {
            // clear the Additional Info field if followup is not Yes
            if (this.model.followupOptions.selectedEntry.value !== ConfigConstants.optionValueYes) {
                this.model.additionalInfo.value = '';
            }
        }

        // https://evorait.atlassian.net/browse/SHCNGMAAP2-413
        // ANZ needs the InfoToCC to be mandatory if followup is set to yes
        if (this.clientManager.isNgmAAANZ()) {
            this.eventService.triggerFollowupOptionChanged(this.showFollowUpAppointment);
        }
        this.changeDetector.detectChanges();
    }

    private determineFollowupDependency(): void {
        this.model.determineFollowupDependency();
        this.showHideControls();
    }

    public getAppointmentSlots(): void {
        this.log.debug('ConfirmationFollowupComponent: getAppointmentSlots: requesting appointment slots');
        this.appointmentBookingService
            .getAppointment(this.model.getAppointmentPayload())
            .pipe(
                takeUntil(this.destroy$),
                take(1),
                switchMap((slots) => {
                    this.log.debug(
                        'ConfirmationFollowupComponent: getAppointmentSlots: received appointment slots successfully',
                    );
                    this.model.setAppointmentSlots(slots);
                    this.updateEnabledDates();
                    return EMPTY;
                }),
                catchError(() => {
                    this.resetAppointment();
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    private updateEnabledDates(): void {
        if (this.model.appointmentSlots.length > 0) {
            const datesEnabled = this.model.appointmentSlots.map((slot) => slot.date.toDate());
            this.datesEnabledSubject.next(datesEnabled);
            this.eventService.triggerAppointmentSlotBooked(true);
        } else {
            this.resetAppointment();
        }
    }

    public resetAppointment(): void {
        if (this.model.availableSlotsDropdownModel) {
            this.log.debug('ConfirmationFollowupComponent: resetAppointment: resetting appointment date and time');
            this.model.appointmentSlots.length = 0;
            this.model.resetAppointmentDateAndTime();
            this.datesEnabledSubject.next([]);
            this.eventService.triggerAppointmentSlotBooked(false);
        }
    }
}
